import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Mail } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { Reveal } from "../components/site/Reveal";
import { EnquiryForm } from "../components/site/EnquiryForm";
import { api, trackEvent } from "../lib/api";
import { useTitle } from "../App";

const SERVICES = [
  "Affiliate Program Strategy", "CPS Programs", "CPA Programs", "CPL Programs",
  "Publisher Recruitment", "Affiliate Network Partnerships", "Brand-Publisher Partnerships",
  "Ecommerce Affiliate Growth", "D2C Affiliate Programs", "Lead Generation Partnerships",
  "Coupon & Cashback Partnerships", "Content Commerce", "Influencer Affiliate Programs",
  "Comparison / Review Publishers", "Sub-Affiliate Partnerships", "Commission Strategy",
  "Partner Onboarding", "Tracking Strategy", "Affiliate Optimization", "Fraud Monitoring",
  "Incrementality Analysis", "Affiliate Reporting",
];

const PRINCIPLES = [
  { n: "01", title: "Structure before scale", text: "Commission strategy, payout design and program rules are set from your margins and payback windows — not copied from a competitor's rate card." },
  { n: "02", title: "Recruitment with intent", text: "We recruit publishers deliberately: content commerce, coupon and cashback, comparison and review sites, influencer affiliates, networks and sub-affiliate partners." },
  { n: "03", title: "Trust, verified", text: "Tracking strategy, validation, fraud monitoring and incrementality analysis ensure payouts fund growth that would not have happened anyway." },
  { n: "04", title: "Reporting finance trusts", text: "Affiliate reporting with the same rigour as paid media — partner-level performance, quality assessment and clear next actions." },
];

export default function Affiliate() {
  useTitle("Affiliate Marketing & Strategic Partnerships");
  const [config, setConfig] = useState({});
  useEffect(() => {
    api.get("/content/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const waNumber = config.whatsapp_number;
  const waMsg = "Hi Ekavrix Team, I would like to discuss an affiliate or strategic partnership.";

  return (
    <div data-testid="affiliate-page" className="bg-midnight text-mist">
      <Navbar />
      <section className="grain relative overflow-hidden pt-[76px]">
        <div className="pointer-events-none absolute -right-40 top-20 h-[28rem] w-[28rem] rounded-full bg-violet/15 blur-[130px]" />
        <div className="container-ek py-24 sm:py-28">
          <Reveal>
            <p className="eyebrow mb-4">Affiliate & Strategic Partnerships · B2B</p>
            <h1 className="max-w-4xl font-display text-4xl font-bold tracking-tight sm:text-6xl">
              Partner-led growth, engineered for incrementality.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Ekavrix designs, builds and manages affiliate and strategic partnership programs for brands that want partner channels to produce real, measurable growth — not just attributed growth. For publishers and networks, we are a serious, well-run program to work with.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact?category=partnership" data-testid="affiliate-cta-primary" className="btn-primary">
                Discuss an Affiliate Partnership <ArrowRight size={15} />
              </Link>
              {waNumber && (
                <a href={`https://wa.me/${waNumber}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noopener noreferrer"
                  data-testid="affiliate-whatsapp" onClick={() => trackEvent("whatsapp_click", { from: "affiliate" })}
                  className="inline-flex items-center gap-2 rounded-full border border-teal/40 px-7 py-3.5 font-display text-sm font-semibold text-teal transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal/10">
                  <MessageCircle size={16} /> WhatsApp Us
                </a>
              )}
              <a href="mailto:business@ekavrixtech.com" data-testid="affiliate-email"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-display text-sm font-semibold text-slate-300 transition-colors hover:border-violet/50 hover:text-white">
                <Mail size={16} /> business@ekavrixtech.com
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 text-slatetext sm:py-28">
        <div className="container-ek">
          <Reveal>
            <p className="eyebrow mb-4 !text-violet">Capability</p>
            <h2 className="max-w-2xl font-display text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">The full partnership stack.</h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slatetext/70">
              Every engagement is scoped to what your business can actually support — not every program needs every component, and we will say so.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s} delay={Math.min(i * 0.03, 0.3)}>
                <div data-testid={`affiliate-service-${i}`} className="flex h-full items-center rounded-xl border border-midnight/[0.08] bg-white px-4 py-3.5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-violet/30">
                  <span className="font-display text-sm font-medium text-midnight">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container-ek">
          <Reveal>
            <p className="eyebrow mb-4">How We Run Programs</p>
            <h2 className="max-w-2xl font-display text-3xl font-semibold tracking-tight sm:text-4xl">Four principles. No grey areas.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="card-dark h-full p-7">
                  <span className="font-mono text-xs text-teal">{p.n}</span>
                  <h3 className="mt-4 font-display text-lg font-semibold">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-deep py-20 sm:py-28">
        <div className="container-ek grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">Start</p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Discuss an Affiliate Partnership</h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
              Whether you are a brand building a program or a publisher looking for a serious partner, tell us what you are working with. Enquiries route directly to our business team.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="card-dark p-7 sm:p-10">
              <EnquiryForm title="Affiliate & partnership enquiry" defaultCategory="partnership" sourcePage="/affiliate" />
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
